import { useEffect, useRef, useState } from 'react';
import ConfigControl from 'components/config-control';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import { cloneDeep } from 'lodash';
import { getConfigItem } from 'util/device';
import { Banner } from '@thermon-platforms/thermon-ui-core';
import { parseErrors } from 'util/api';
import RtdAssignment from './rtd-assignment.jsx';


function ConfigureDeviceModal({device, parent, dismiss}) {
  const scrollBody = useRef();
  const [loading, setLoading] = useState(false);
  const [deviceTemp, setDeviceTemp] = useState(cloneDeep(device));
  const [updatedConfiguration, setUpdatedConfiguration] = useState([]);
  const [, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [type, setType] = useState();
  const [errors, setErrors] = useState();
  const [success, setSuccess] = useState();

  useEffect(() => {
    const deviceType = deviceTypesStore.types.find(t => device.deviceModel == t.id);
    // If the device is new, let's pre-populate the configuration
    if (!deviceTemp.id) {
      setLoading(true);
      devicesStoreActions.fetchCreationSettings(parent.id, deviceType.id)
        .then(items => {
          // If the device has creation settings let's use those
          if (items.length) {
            setDeviceTemp(Object.assign({}, deviceTemp, {
              configuration: items
            }));
            // Let's make a pseudo type to handle creation
            setType({
              name: deviceType.name,
              configuration: [{
                section: 'Creation Settings',
                items
              }]
            });
            setUpdatedConfiguration([...items]);
          } else {
            // Otherwise, we can default to standard configuration
            setType(deviceType);
            type?.configuration?.forEach(section => {
              section.items.forEach(item => {
                deviceTemp.configuration.push({
                  key: item.key,
                  value: item.default
                })
              });
            });
            // I dunno, this is dumb but I don't feel like writiing a multilayer map right now
            setDeviceTemp({...deviceTemp});
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setType(deviceType);
    }
  }, []);

  async function finish() {
    setLoading(true);
    setErrors(undefined);
    let promise;

    // If it's a new device we need to add it to the parent model, otherwise we can just save it
    if (parent && !deviceTemp.id) {
      promise = devicesStoreActions.addChild(Object.assign({}, deviceTemp, {
        name: getConfigItem(deviceTemp, 'Name')?.value,
        configuration: updatedConfiguration
      }), parent);
    } else {
      promise = devicesStoreActions.update({
        id: deviceTemp.id,
        name: deviceTemp.name,
        configuration: updatedConfiguration.map(item => {
          const existingItem = deviceTemp.configuration.find(i => i.key == item.key);
          // I have no idea why this can't be handled in the backend but here we are
          if (item.unit) {
            return {
              key: item.key,
              value: item.value,
              unit: existingItem.unit
            }
          } else {
            return item;
          }
        }),
        type: deviceTemp.type,
      });
    }

    promise
      .then(updatedDevice => {
        /*
          Hack to get around certain devices not returning configuration data initially after save
        */
        if (!updatedDevice.configuration) {
          updatedDevice.configuration = [getConfigItem(deviceTemp, 'Name')];
        }
        /* / */
        setDeviceTemp(updatedDevice);

        // Since the initial type could be contrived from creation settings
        // let's update it to the real type
        if (!type.id) {
          setType(deviceTypesStore.types.find(t => updatedDevice.deviceModel == t.id));
        }

        setUpdatedConfiguration([]);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 10 * 1000);
      })
      .catch(async res => {
        try {
          setErrors(parseErrors(await res.json()));
        } catch {
          setErrors(res);
        }
      })
      .finally(() => {
        scrollBody.current?.scrollTo({top: 0, behavior: 'smooth'});
        setLoading(false);
      });
  }

  const blockList = ['LockoutTagOut'];

  /**
   * @function setValue
   * Handle setting a deeply nested configuration value
   *
   * @param {*} value
   * @param {string} key
   * @param {number} index
   */
  function setValue(value, key) {
    const item = getConfigItem({configuration: updatedConfiguration}, key);
    if (item) {
      item.value = value;
    } else {
      updatedConfiguration.push({key, value});
    }
    setUpdatedConfiguration([...updatedConfiguration]);
  }

  return (
    <I18nProvider i18n={i18n}>
      <header>
        {deviceTemp.id ?
          <h3><Trans>Configure</Trans> {deviceTemp.type}: <strong>{getConfigItem(deviceTemp, 'Name')?.value}</strong></h3> :
          <h3><Trans>Configure New</Trans> {type?.name}</h3>
        }
      </header>
      <section ref={scrollBody}>
        <Banner show={errors} status="alert" small={true}>
          {errors && typeof errors == 'string' ?
            errors : <Trans>There was an issue saving this {device.type}.</Trans>
          }
        </Banner>
        <Banner show={success} status="ok" small={true}>
          <Trans>Settings changes have been requested.</Trans>
        </Banner>
        {[].concat(type?.configuration || []).sort((a, b) => a.items[0]?.categoryOrder - b.items[0]?.categoryOrder)
          .map(section => {
            // Firmware appears to be read only
            if (section.section == 'Firmware' || section.section == 'Circuit') return null;
            return (
              <div className="configuration-section" key={section.section}>
                <h4>{section.section}</h4>
                <ul className="form">
                  {section.items.map((item, i) => {
                    if (blockList.includes(item.key)) return null;
                    return (
                      <li key={`config-${section.section}-${i}`}>
                        <ConfigControl
                          item={item}
                          error={errors?.[item.key]}
                          onChange={val => {
                            setValue(val, item.key);
                            if (errors?.[item.key]) {
                              delete errors?.[item.key];
                              setErrors(Object.assign({}, errors));
                            }
                          }}
                          value={getConfigItem(deviceTemp, item.key)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        }
        {type?.name == 'SteamController' ? <RtdAssignment device={deviceTemp} /> : null}
      </section>
      <footer>
        <button onClick={dismiss}>
          <Trans>Close</Trans>
        </button>
        <button
          className={`primary ${loading ? 'processing' : ''}`}
          onClick={finish}
          disabled={loading || (updatedConfiguration.length == 0 && deviceTemp.id)}
        >
          <Trans>Save</Trans>
        </button>
      </footer>
    </I18nProvider>
  );
}

export default ConfigureDeviceModal;
